import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Compléments du tiroir de panier : « On y ajoute ? » et rappel de
 * réassurance.
 *
 * Le tiroir est re-rendu par morphing à chaque mise à jour du panier : aucune
 * référence de noeud n'est conservée, tout est retrouvé à chaque passage.
 */

const HOST = 'lrs-cart-extras';
const MAX = 3;

const root = () => document.querySelector(HOST);

function drawer() {
  return document.querySelector('cart-drawer-component');
}

/** Identifiants produits déjà au panier : on ne recommande pas ce qu'on a. */
async function cartState() {
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    if (!res.ok) return { ids: [], count: 0 };
    const cart = await res.json();
    return { ids: cart.items.map((i) => i.product_id), count: cart.item_count, first: cart.items[0] };
  } catch {
    return { ids: [], count: 0 };
  }
}

function fallbackItems(host) {
  const tpl = host.querySelector('[data-lrs-fallback]');
  if (!tpl) return [];
  return Array.from(tpl.content.querySelectorAll('.lrs-xa__i')).map((n) => n.cloneNode(true));
}

async function recommendedItems(productId) {
  if (!productId) return [];
  try {
    const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=6&intent=related`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.products) ? data.products : [];
  } catch {
    return [];
  }
}

function nodeFromProduct(product, addLabel) {
  const variant = (product.variants || []).find((v) => v.available) || product.variants?.[0];
  if (!variant) return null;

  const wrap = document.createElement('div');
  wrap.className = 'lrs-xa__i';
  wrap.dataset.productId = String(product.id);
  wrap.dataset.variantId = String(variant.id);

  const image = product.featured_image || product.images?.[0] || '';
  wrap.innerHTML = `
    <a class="lrs-xa__m" href="${product.url}" tabindex="-1" aria-hidden="true">
      ${image ? `<img class="lrs-xa__img" src="${image}" alt="" width="56" height="70" loading="lazy">` : ''}
    </a>
    <div class="lrs-xa__b">
      <a class="lrs-xa__t" href="${product.url}"></a>
      <span class="lrs-xa__p"></span>
    </div>
    <button type="button" class="lrs-xa__add" data-lrs-xa-add aria-label="${addLabel}">+</button>`;

  // Le titre et le prix passent par textContent : jamais d'injection HTML
  // depuis une réponse d'API.
  wrap.querySelector('.lrs-xa__t').textContent = product.title;
  wrap.querySelector('.lrs-xa__p').textContent = formatMoney(variant.price);
  return wrap;
}

function formatMoney(cents) {
  const value = Number(cents) / 100;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
}

let lastKey = '';

async function render() {
  const host = root();
  const shell = drawer();
  if (!host || !shell) return;

  const { ids, count, first } = await cartState();

  // Panier vide : le tiroir n'a pas de résumé, donc rien à compléter en pied.
  // Il lui manque en revanche deux portes de sortie.
  if (count === 0) {
    lastKey = '0:';
    shell.querySelector('.lrs-xa')?.remove();
    shell.querySelector('.lrs-xt')?.remove();
    empty(host, shell);
    return;
  }

  const summary = shell.querySelector('.cart-drawer__summary');
  if (!summary) return;

  // Garde-fou : le tiroir est observé, et nos propres insertions le mutent.
  // Sans cette signature, chaque rendu en déclencherait un autre.
  const key = `${count}:${ids.join(',')}`;
  if (key === lastKey && shell.querySelector('.lrs-xa')) return;
  lastKey = key;

  shell.querySelector('.lrs-xa')?.remove();
  shell.querySelector('.lrs-xt')?.remove();
  shell.querySelector('.lrs-pn__empty')?.remove();

  let nodes = [];
  const recos = await recommendedItems(first?.product_id);
  if (recos.length) {
    nodes = recos
      .filter((p) => !ids.includes(p.id))
      .map((p) => nodeFromProduct(p, host.dataset.addLabel || 'Ajouter'))
      .filter(Boolean);
  }
  if (nodes.length < MAX) {
    const extra = fallbackItems(host).filter(
      (n) => !ids.includes(Number(n.dataset.productId)) && !nodes.some((x) => x.dataset.productId === n.dataset.productId)
    );
    nodes = nodes.concat(extra);
  }
  nodes = nodes.slice(0, MAX);
  if (!nodes.length) return;

  const block = document.createElement('div');
  block.className = 'lrs-xa';
  const title = document.createElement('p');
  title.className = 'lrs-xa__h';
  title.textContent = host.dataset.heading || 'On y ajoute ?';
  block.appendChild(title);
  nodes.forEach((n) => block.appendChild(n));
  summary.parentElement.insertBefore(block, summary);

  footer(host, summary);
}

const ICONS = {
  lock: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>',
  back: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/></svg>',
  person: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>',
};

/**
 * Compléments du pied : la mention de TVA, le retour à la boutique et la
 * ligne de réassurance. Le résumé d'Horizon n'expose aucun point d'insertion
 * et n'est pas un block : on le complète après coup, comme le reste.
 */
function footer(host, summary) {
  summary.querySelector('.lrs-pn__tva')?.remove();
  summary.querySelector('.lrs-pn__back')?.remove();
  summary.querySelector('.lrs-pn__trust')?.remove();

  const cta = summary.querySelector('.cart__checkout-button');
  const tvaText = host.dataset.tva || '';
  if (tvaText && cta) {
    const tva = document.createElement('p');
    tva.className = 'lrs-pn__tva';
    tva.textContent = tvaText;
    cta.parentElement.insertBefore(tva, cta);
  }

  const backLabel = host.dataset.backLabel || '';
  if (backLabel) {
    const back = document.createElement('a');
    back.className = 'lrs-pn__back';
    back.href = host.dataset.backUrl || '/collections/all';
    back.textContent = backLabel;
    summary.appendChild(back);
  }

  const parts = (host.dataset.trust || '').split('·').map((t) => t.trim()).filter(Boolean);
  if (parts.length) {
    const keys = ['lock', 'back', 'person'];
    const trust = document.createElement('p');
    trust.className = 'lrs-pn__trust';
    parts.forEach((text, i) => {
      const span = document.createElement('span');
      span.innerHTML = ICONS[keys[i % keys.length]];
      span.appendChild(document.createTextNode(text));
      trust.appendChild(span);
    });
    summary.appendChild(trust);
  }
}

/**
 * Panier vide : un aplat écru, une phrase courte et deux portes de sortie.
 * Le gabarit d'Horizon n'affichait qu'un titre et une invitation à se
 * connecter, ce qui laisse le visiteur sans rien à faire.
 */
function empty(host, shell) {
  const zone = shell.querySelector('.cart-drawer__content, .cart-items__wrapper');
  if (!zone || zone.querySelector('.lrs-pn__empty')) return;

  const links = [
    [host.dataset.emptyLink1Label, host.dataset.emptyLink1Url],
    [host.dataset.emptyLink2Label, host.dataset.emptyLink2Url],
  ].filter(([label, url]) => label && url);

  const box = document.createElement('div');
  box.className = 'lrs-pn__empty';
  box.innerHTML =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h2l2.2 10.2a2 2 0 0 0 2 1.6h7.4a2 2 0 0 0 2-1.6L20 9H6"/><circle cx="10" cy="20" r="1.2"/><circle cx="17" cy="20" r="1.2"/></svg>' +
    '<h2></h2><p></p>';
  box.querySelector('h2').textContent = host.dataset.emptyTitle || 'Votre panier est vide';
  box.querySelector('p').textContent = host.dataset.emptyText || '';

  if (links.length) {
    const row = document.createElement('div');
    row.className = 'lrs-pn__links';
    links.forEach(([label, url]) => {
      const a = document.createElement('a');
      a.href = url;
      a.textContent = label;
      row.appendChild(a);
    });
    box.appendChild(row);
  }

  // Le titre natif ferait doublon avec celui de l'encadré.
  zone.querySelector('#cart-drawer-heading')?.setAttribute('hidden', '');
  zone.querySelector('.cart-items__wrapper--drawer-empty')?.setAttribute('hidden', '');
  zone.prepend(box);
}

async function add(variantId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!res.ok) throw new Error(String(res.status));
    deferred.resolve({ detail: { didError: false } });
  } catch {
    deferred.resolve({ detail: { didError: true } });
  }
  render();
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-lrs-xa-add]');
  if (!button) return;
  event.preventDefault();
  const id = button.closest('.lrs-xa__i')?.dataset.variantId;
  if (id) add(id);
});

/**
 * Le tiroir est re-rendu par morphing, après l'évènement de panier et sans
 * signal de fin. On observe donc ses mutations plutôt que de parier sur un
 * délai : c'est la seule façon d'insérer au bon moment à tous les coups.
 */
let pending = 0;
const schedule = () => {
  window.clearTimeout(pending);
  pending = window.setTimeout(render, 120);
};

function watch() {
  const shell = drawer();
  if (!shell || shell.dataset.lrsXaWatched === 'on') return;
  shell.dataset.lrsXaWatched = 'on';
  new MutationObserver(schedule).observe(shell, { childList: true, subtree: true });
  schedule();
}

watch();
document.addEventListener('shopify:cart:lines-update', () => {
  lastKey = '';
  schedule();
});
document.addEventListener('shopify:section:load', () => {
  watch();
  schedule();
});
window.addEventListener('load', watch);
